<template>
    <div class="mb-8 space-y-2 max-w-7xl mx-auto">
        <div class="flex items-center justify-between bg-gray-50 py-2 px-3">
            <h4 class="text-lg text-center font-medium">{{ $t('Invites') }}</h4>
            <div class="flex justify-end">
                <button
                    class="bg-blue-500 text-white border border-blue-500 hover:border-blue-600 hover:bg-white hover:text-blue-800 transition duration-150 text-sm rounded px-3 py-2"
                    @click="open = true"
                >
                    {{ $t('Invite User') }}
                </button>
            </div>
        </div>

        <modal :closable="true" @close="closeModal" :title="$t('Invite User')" :open="open">
            <div slot="content">
                <div v-if="submitted" class="my-12 flex flex-col items-center">
                    <span>{{ $t('Password Reset Link') }}</span>
                    <a :href="createdMemo.passwordResetLink" class="text-base">{{
                        createdMemo.passwordResetLink
                    }}</a>
                </div>
                <form
                    v-else
                    class="md:pt-6 md:pb-4 space-y-2 md:space-y-4 flex flex-col"
                    style="min-height: 50vh;"
                    @submit.prevent="createInvite"
                >
                    <div class="space-y-5">
                        <section class="w-full flex-1 space-y-1 flex flex-col">
                            <label for="formArea" class="text-sm">{{ $t('Area') }}</label>
                            <v-select
                                :appendToBody="true"
                                style="z-index: 9999"
                                class="flex-1 style-select relative z-0 dropdown-container"
                                :placeholder="$t('Area')"
                                :options="areas"
                                v-model="formArea"
                            >
                                <template v-slot:option="option" class="dropdown-menu">
                                    <div
                                        class="dropdown-menu w-full py-3 flex items-center space-x-3 focus:outline-none overflow-x-hidden"
                                    >
                                        <span class="text-sm ml-2">
                                            {{ option.tag }}
                                        </span>
                                    </div>
                                </template>
                            </v-select>
                        </section>
                        <section class="w-full flex-1 flex flex-col">
                            <label for="formEmail" class="text-sm">{{ $t('Email') }}</label>
                            <input
                                id="formEmail"
                                type="email"
                                v-model="formEmail"
                                class="py-3 px-4 border border-gray-400 rounded"
                            />
                        </section>
                        <div class="flex items-center gap-2">
                            <section class="w-full flex-1 flex flex-col">
                                <label for="formUserName" class="text-sm">{{ $t('Name') }}</label>
                                <input
                                    id="formUserName"
                                    v-model="formUserName"
                                    class="py-3 px-4 border border-gray-400 rounded"
                                />
                            </section>
                            <section class="w-full flex-1 flex flex-col">
                                <label for="formJob" class="text-sm">{{ $t('Job') }}</label>
                                <input
                                    id="formJob"
                                    v-model="formJob"
                                    class="py-3 px-4 border border-gray-400 rounded"
                                />
                            </section>
                        </div>
                        <div class="flex items-center gap-2">
                            <section class="w-full flex-1 space-y-1 flex flex-col">
                                <label for="formUserSkills" class="text-sm">{{
                                    $t('Skills')
                                }}</label>
                                <v-select
                                    :appendToBody="true"
                                    class="flex-1 style-select relative z-0"
                                    :placeholder="$t('Skills')"
                                    :options="skills"
                                    v-model="formUserSkills"
                                    multiple
                                >
                                    <template v-slot:option="option">
                                        <div
                                            class="w-full py-3 flex items-center space-x-3 focus:outline-none overflow-x-hidden"
                                        >
                                            <span class="text-sm ml-2">
                                                {{ option.tag }}
                                            </span>
                                        </div>
                                    </template>
                                </v-select>
                            </section>
                        </div>
                        <section class="w-full flex-1 space-y-1 flex flex-col">
                            <label for="groups" class="text-sm">{{ $t('Groups') }}</label>
                            <v-select
                                :appendToBody="true"
                                class="flex-1 style-select !relative !z-0"
                                placeholder="User Groups"
                                :options="groups"
                                v-model="formGroups"
                                multiple
                            >
                                <template v-slot:option="option">
                                    <div
                                        class="z-50 w-full py-3 flex items-center space-x-3 focus:outline-none overflow-x-hidden"
                                    >
                                        <span class="text-sm ml-2">
                                            {{ option.tag }}
                                        </span>
                                    </div>
                                </template>
                            </v-select>
                        </section>
                        <section class="w-full flex-1 space-y-1 flex flex-col">
                            <label for="memo" class="text-sm">{{ $t('Memo') }}</label>
                            <textarea
                                :disabled="submitting || submitted"
                                v-model="formMemo"
                                name="memo"
                                class="form-textarea"
                            />
                        </section>
                    </div>
                    <section class="space-y-1 flex flex-col">
                        <span
                            v-if="formError"
                            class="bg-red-100 text-center text-sm py-1 px-1 rounded shake text-red-900 max-w-3xl overflow-auto"
                        >
                            {{ this.formError }}
                        </span>
                    </section>
                    <div class="flex-1 flex flex-col justify-end">
                        <button
                            class="flex items-center justify-center w-full border border-transparent hover:bg-transparent hover:border-blue-500 hover:text-gray-900 transition duration-150 font-medium text-sm text-white px-4 py-3 rounded focus:outline-none focus:shadow-outline"
                            :class="[
                                submitting || !this.formValid
                                    ? 'pointer-events-none bg-gray-200'
                                    : 'bg-blue-500',
                            ]"
                            type="submit"
                        >
                            <spinner v-if="submitting" class="text-white" />
                            <span v-else>{{ $t('Invite User') }}</span>
                        </button>
                    </div>
                </form>
            </div>
        </modal>

        <ul class="space-y-1">
            <li v-for="code in invites" :key="code.id">
                <InviteCard :invitationCode="code" :groups="groups" />
            </li>
        </ul>
        <spinner v-if="!invites && loading" />
        <div v-else>
            <div v-if="hasMore" class="flex justify-center">
                <button
                    @click="showMore"
                    class="border flex justify-center items-center h-10 w-40 border-gray-400 hover:border-gray-800 text-sm rounded px-3 py-2"
                >
                    <spinner v-if="loadingMore" class="text-white" />
                    <span v-else>{{ $t('Load More') }}</span>
                </button>
            </div>
            <div v-else class="flex justify-center">
                <span>{{ $t('No more items to show') }}</span>
            </div>
        </div>
    </div>
</template>
<script>
import api from '../lib/api'

import InviteCard from '../components/InviteCard'
export default {
    components: {
        InviteCard,
    },
    async mounted() {
        const [invites, newOffset] = await api.getInvites(this.limit, null)
        this.groups = this.makeSelectableList(await api.getGroups())
        this.skills = this.makeSelectableList(await api.getSkills())
        this.areas = this.makeSelectableList(await api.getAreas())
        this.invites = invites
        this.offset = newOffset
        this.loading = false
    },
    methods: {
        makeSelectableList(items) {
            return items ? items.map(i => ({ label: i.tag, ...i })) : []
        },
        async showMore() {
            this.loadingMore = true
            const [moreInvites, newOffset] = await api.getInvites(this.limit, this.offset)
            this.invites = [...this.invites, ...moreInvites]
            this.offset = newOffset
            this.loadingMore = false
        },
        async createInvite() {
            this.submitting = true
            this.formError = null
            try {
                const groups = this.formGroups.map(s => s.id)
                const skills = this.formUserSkills.map(s => s.id)
                const data = {
                    email: this.formEmail,
                    areaId: this.formArea.id,
                    job: this.formJob,
                    name: this.formUserName,
                    memo: this.formMemo,
                    groups,
                    skills,
                }
                const resp = await api.createInvite(data)
                this.submitting = false
                this.submitted = true
                this.createdMemo = resp
            } catch (err) {
                console.log(err)
                this.submitting = false
                this.formError = err.message
            }
        },
        closeModal() {
            this.formError = null
            this.open = false
            if (this.submitted) {
                this.$router.go()
            }
        },
    },
    computed: {
        formValid() {
            return !!this.formMemo && this.formMemo !== '' && this.formGroups.length
        },
    },
    data() {
        return {
            invites: [],
            groups: [],
            skills: [],
            areas: [],
            loading: true,
            createdMemo: null,
            formError: null,
            formMemo: '',
            formGroups: [],
            formJob: '',
            formUserName: '',
            formArea: null,
            formEmail: '',
            formUserSkills: [],
            formUserInterests: [],
            submitting: false,
            submitted: false,
            loadingMore: false,
            open: false,
            limit: 20,
            offset: null,
            hasMore: true,
        }
    },
}
</script>
